import React, { useState } from "react";
import { Link } from "react-router-dom";

import { getOptions } from "utils/fields";
import { getNetTotalEnergyConsumption } from "utils/results";
import { getF280DesignHeatingTemp } from "utils/weather";

import Select from "components/Input/Select";

import classes from "./styles.module.scss";

const SummaryStat = ({ label, value, unit, decimals = 2 }) => (
    <div className={classes.stat}>
        <p>{label}</p>
        <span>
            <strong>{typeof value === "number" ? value.toFixed(decimals).replace("-0.00", "0.00") : value}</strong>
            {unit && typeof value === "number" ? ` ${unit}` : ""}
        </span>
    </div>
);

const heatLossAtOutdoorTemp = (heatLoss, designTemp, outdoorTemp) => {
    if (!heatLoss || designTemp == null) return 0;
    if (outdoorTemp >= 16) return 0;
    return Math.max(0, (heatLoss * (16 - outdoorTemp)) / (16 - designTemp));
};

const OutdoorHeatLoss = ({ watts, unit, designTemp }) => {
    const [outdoorTemp, setOutdoorTemp] = useState(-5);
    const [outdoorTempInput, setOutdoorTempInput] = useState("-5");
    const min = Math.min(designTemp, -22) || -40;
    const heatLoss = heatLossAtOutdoorTemp(watts, designTemp, outdoorTemp);
    const value = typeof heatLoss === "number" && heatLoss ? (unit === "W" ? heatLoss : heatLoss * 3.41214) : "N/A";

    return (
        <div className={classes.stat}>
            <p className={classes.outdoorLabel}>
                Heat Loss at Outdoor Temp (°C):
                <input
                    type="number"
                    value={outdoorTempInput}
                    min={min}
                    max={16}
                    step={1}
                    onChange={(event) => {
                        const inputValue = event.target.value;
                        setOutdoorTempInput(inputValue);
                        const parsed = parseFloat(inputValue);
                        if (!isNaN(parsed) && inputValue !== "" && inputValue !== "-") {
                            setOutdoorTemp(parsed);
                        }
                    }}
                    onBlur={(event) => {
                        const parsed = parseFloat(event.target.value);
                        const next = !isNaN(parsed)
                            ? Math.min(Math.max(parsed, min), 16)
                            : Math.min(Math.max(outdoorTemp, min), 16);
                        setOutdoorTemp(next);
                        setOutdoorTempInput(next.toString());
                    }}
                />
            </p>
            <span>
                <strong>{typeof value === "number" ? value.toFixed(0).replace("-0", "0") : value}</strong>
                {unit && typeof value === "number" ? ` ${unit}` : ""}
            </span>
        </div>
    );
};

const ReadinessItem = ({ status, title, detail, linkTo }) => (
    <li className={classes.readinessItem}>
        <span
            className={`${classes.status} ${
                status === "pass" ? classes.pass : status === "fail" ? classes.fail : classes.pending
            }`}
        >
            {status === "pass" ? "Passing" : status === "fail" ? "Needed" : "Checking"}
        </span>
        <span className={classes.detail}>
            {linkTo ? (
                <Link to={linkTo} className={classes.titleLink}>
                    <strong>{title}</strong>
                </Link>
            ) : (
                <strong>{title}</strong>
            )}
            {detail ? ` ${detail}` : ""}
        </span>
    </li>
);

const Setup = ({
    modelId,
    ready,
    runActive,
    statusMessage,
    packageOptions,
    upgradePackageId,
    onPackageChange,
    hybridCase,
    h2kVersion,
    versionOk,
    packageIsMissing,
    hasHot2000Results,
    resultsStatus,
    resultsLoading,
    resultsError,
    modelData,
    packageResults,
    resultsPackageId,
    replaceWarning,
    sweepLocked,
}) => {
    const resultsCaseKey = resultsPackageId === "base" ? "generalBaseCase" : "generalAllUpgrades";
    const caseResults = packageResults?.[resultsPackageId]?.resultsData?.[resultsCaseKey] || {};
    const { zeroFloorTotal } = getNetTotalEnergyConsumption(caseResults);
    const annualEmissions = caseResults?.resultsSummary?.annualEmissions;
    const annualHeating = caseResults?.resultsSummary?.endUseChart?.spaceHeating;
    const annualElectricity = caseResults?.resultsSummary?.fuelTypeChart?.electricity;
    const annualBackupFuel = caseResults?.resultsSummary?.fuelTypeChart?.[hybridCase.fuelKey];
    const backupFuelLabel = {
        naturalGas: "Natural gas use",
        propane: "Propane use",
        oil: "Heating oil use",
    }[hybridCase.fuelKey];
    const heatLossWatts = caseResults?.f280Results?.heatLoss;
    const sensibleGainWatts = caseResults?.f280Results?.sensHeatGain;
    const latentGainWatts = caseResults?.f280Results?.latHeatGain;
    const hasCaseResults = Boolean(packageResults?.[resultsPackageId]?.metadata?.mode);
    const hasF280Results = typeof heatLossWatts === "number";
    const primaryUnits = modelData?.uiSettings?.primaryUnits || "Metric";
    const heatUnit = primaryUnits === "Metric" ? "W" : "Btu/h";
    const regionId = modelData?.location?.weather?.region?.id;
    const weatherLocationId = modelData?.location?.weather?.location?.id;
    const heatingDegreeDays = modelData?.location?.weather?.location?.value;
    const regionLabel =
        getOptions({ fieldKey: "weatherRegion", version: h2kVersion }).find(({ value }) => value?.id === regionId)
            ?.label || "—";
    const locationLabel =
        getOptions({ fieldKey: "weatherLocation", indKey: regionId, version: h2kVersion }).find(
            ({ value }) => value?.id === weatherLocationId
        )?.label || "—";
    const designHeatingTemp = getF280DesignHeatingTemp(modelData?.location?.weather?.f280?.location?.id || 0);
    const displayHeat = (watts) =>
        typeof watts === "number" && watts ? (primaryUnits === "Metric" ? watts : watts * 3.41214) : "N/A";

    return (
        <div className={classes.panel}>
            <p className={classes.runNote}>{statusMessage}</p>
            <div className={classes.setupTop}>
                <div className={classes.setupCase}>
                    <Select
                        name="hybridUpgradePackage"
                        options={packageOptions}
                        label="Case"
                        placeholder="Base case"
                        large
                        disabled={!ready || runActive}
                        input={{
                            value: upgradePackageId,
                            onChange: onPackageChange,
                        }}
                    />
                    {replaceWarning ? <p className={classes.replaceWarning}>{replaceWarning}</p> : null}
                    {sweepLocked ? (
                        <p className={classes.replaceWarning}>
                            This case already has results. Choose a different case to run that package. If you change
                            the model, use Re-run analysis on the Results tab when you want a new sweep.
                        </p>
                    ) : null}
                </div>
                <div className={classes.setupReadiness}>
                    <h3 className={classes.readinessHeading}>Analysis readiness checklist</h3>
                    <ul className={classes.readiness}>
                        <ReadinessItem
                            status={versionOk ? "pass" : "fail"}
                            title="Valid HOT2000 version:"
                            linkTo={modelId ? `/models/${modelId}/` : undefined}
                            detail={
                                versionOk
                                    ? `${h2kVersion} can run this analysis.`
                                    : "This analysis uses the EnergyPlus workflow, which runs on v11.11, v11.12, and v11.13."
                            }
                        />
                        <ReadinessItem
                            status={resultsStatus}
                            title="Has HOT2000 results:"
                            linkTo={modelId ? `/models/${modelId}/review` : undefined}
                            detail={
                                hasHot2000Results
                                    ? "A completed HOT2000 simulation is available for this case."
                                    : resultsLoading
                                      ? "Looking for simulation results for this case."
                                      : resultsError === "Results not found."
                                        ? "Run HOT2000 for this case and return here after results are saved."
                                        : "Run HOT2000 for this case before starting the hybrid analysis."
                            }
                        />
                        <ReadinessItem
                            status={packageIsMissing ? "fail" : hybridCase.heatPumpOk ? "pass" : "fail"}
                            title="Has a heat pump:"
                            linkTo={modelId ? `/models/${modelId}/mechanicals` : undefined}
                            detail={
                                packageIsMissing
                                    ? "The selected upgrade package is no longer on this model. The case is switching back to the base."
                                    : hybridCase.heatPumpOk
                                      ? hybridCase.heatPumpLabel
                                      : hybridCase.heatPumpDetail
                            }
                        />
                        <ReadinessItem
                            status={packageIsMissing ? "fail" : hybridCase.backupOk ? "pass" : "fail"}
                            title="Has a fossil-fuel backup:"
                            linkTo={modelId ? `/models/${modelId}/mechanicals` : undefined}
                            detail={
                                packageIsMissing
                                    ? "Choose a case that is still on this model."
                                    : hybridCase.backupOk
                                      ? hybridCase.backupLabel
                                      : hybridCase.backupDetail
                            }
                        />
                    </ul>
                </div>
            </div>
            <div className={classes.summarySection}>
                <h3>Mechanical system</h3>
                <dl className={classes.config}>
                    <div>
                        <dt>Heating</dt>
                        <dd>{hybridCase.heatingConfiguration}</dd>
                    </div>
                    <div>
                        <dt>Heat pump</dt>
                        <dd>{hybridCase.heatPumpLabel}</dd>
                    </div>
                    {hybridCase.savedCutoffLabel ? (
                        <div>
                            <dt>Saved cutoff</dt>
                            <dd>{hybridCase.savedCutoffLabel}</dd>
                        </div>
                    ) : null}
                </dl>
            </div>
            <div className={classes.summarySection}>
                <h3>Location</h3>
                <dl className={classes.config}>
                    <div>
                        <dt>Location</dt>
                        <dd>{locationLabel}</dd>
                    </div>
                    <div>
                        <dt>Region</dt>
                        <dd>{regionLabel}</dd>
                    </div>
                    <div>
                        <dt>Heating degree days</dt>
                        <dd>
                            {heatingDegreeDays == null ||
                            heatingDegreeDays === "" ||
                            Number.isNaN(Number(heatingDegreeDays))
                                ? "—"
                                : Number(heatingDegreeDays)}
                        </dd>
                    </div>
                    <div>
                        <dt>Heating design temperature</dt>
                        <dd>{designHeatingTemp == null ? "—" : `${designHeatingTemp}°C`}</dd>
                    </div>
                </dl>
            </div>
            <div className={classes.summarySection}>
                <h3>HOT2000 results for this case</h3>
                {hasCaseResults ? (
                    <div className={classes.stats}>
                        <SummaryStat label="Total annual energy" value={zeroFloorTotal} unit="GJ" />
                        <SummaryStat
                            label="Space heating energy"
                            value={typeof annualHeating === "number" ? annualHeating : "—"}
                            unit="GJ"
                        />
                        <SummaryStat
                            label="Electricity use"
                            value={typeof annualElectricity === "number" ? annualElectricity : "—"}
                            unit="GJ"
                        />
                        {backupFuelLabel ? (
                            <SummaryStat
                                label={backupFuelLabel}
                                value={typeof annualBackupFuel === "number" ? annualBackupFuel : "—"}
                                unit="GJ"
                            />
                        ) : null}
                        <SummaryStat
                            label="Annual emissions"
                            value={typeof annualEmissions === "number" ? annualEmissions : "—"}
                            unit="tCO₂"
                        />
                    </div>
                ) : (
                    <p className={classes.summaryEmpty}>
                        {resultsLoading
                            ? "Looking for HOT2000 results for this case."
                            : "Run HOT2000 for this case to see annual energy and emissions."}
                    </p>
                )}
            </div>
            <div className={classes.summarySection}>
                <h3>CSA F280-12 results</h3>
                {hasF280Results ? (
                    <div className={classes.f280Stats}>
                        <SummaryStat
                            label="Design Heat Loss"
                            value={displayHeat(heatLossWatts)}
                            unit={heatUnit}
                            decimals={0}
                        />
                        {designHeatingTemp != null && (
                            <OutdoorHeatLoss watts={heatLossWatts} unit={heatUnit} designTemp={designHeatingTemp} />
                        )}
                        <SummaryStat
                            label="Design Heat Gain (Sensible + Latent)"
                            value={displayHeat(latentGainWatts)}
                            unit={heatUnit}
                            decimals={0}
                        />
                        <SummaryStat
                            label="Design Heat Gain (Sensible)"
                            value={displayHeat(sensibleGainWatts)}
                            unit={heatUnit}
                            decimals={0}
                        />
                    </div>
                ) : (
                    <p className={classes.summaryEmpty}>
                        {resultsLoading
                            ? "Looking for F280 results for this case."
                            : "F280 results for this case are not available yet."}
                    </p>
                )}
            </div>
        </div>
    );
};

export default Setup;
